// web/src/scene/rtls/AccessPointMarkers.tsx
//
// Renders access-point markers on the 3D scene. Visually distinct from
// asset/location markers per the UI-design spec:
//   - Hexagon body (vs circular locations / GLB assets)
//   - WiFi-arc icon (three concentric arcs above a dot)
//   - Status-colored border ring + corner dot
//   - Size 10 (vs assets=18, locations=14) — smallest marker class
//   - No pulse / ground glow / ground snap / proximity spread — APs are
//     static infrastructure at operator-supplied coordinates.

import React, { useEffect, useMemo, useRef, useState } from 'react'
import * as THREE from 'three'
import { useAppSelector } from '../../app/useAppSelector'
import { useMarkerActions } from './useMarkerActions'
import { MarkerHoverLabel } from './MarkerHoverLabel'
import type { AccessPoint, AccessPointStatus } from '@shared/types/rtlsTypes'

const HOVER_DELAY_MS = 200

const AP_STATUS_LABEL: Record<AccessPointStatus, string> = {
  active: 'Active',
  offline: 'Offline',
  fault: 'Fault',
}

// Status → stroke/dot color. Matches panel list dot colors.
const STATUS_RGB: Record<AccessPointStatus, string> = {
  active:  '#22c55e',  // green
  offline: '#9ca3af',  // muted grey
  fault:   '#ef4444',  // red
}

const MARKER_SIZE = 10
// Small lift so the hex sprite clears the ground mesh's depth buffer
// without visibly floating.
const MARKER_Z_OFFSET = 1
const LABEL_Z_OFFSET = MARKER_Z_OFFSET + MARKER_SIZE / 2 + 3
const LABEL_HEIGHT = 4

// Draw a regular hexagon (flat-top) filled with a dark background and
// stroked in the status color, with a wifi-arcs icon centered inside.
function drawAccessPointTexture(status: AccessPointStatus): HTMLCanvasElement {
  const size = 256
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')!
  const cx = size / 2
  const cy = size / 2
  const r = size * 0.42

  ctx.clearRect(0, 0, size, size)

  // Hexagon path
  const hexPath = (offset = 0) => {
    ctx.beginPath()
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 3) * i - Math.PI / 2 // flat-top
      const px = cx + (r + offset) * Math.cos(angle)
      const py = cy + (r + offset) * Math.sin(angle)
      if (i === 0) ctx.moveTo(px, py)
      else ctx.lineTo(px, py)
    }
    ctx.closePath()
  }

  // Shadow under hex
  ctx.save()
  ctx.shadowColor = 'rgba(0, 0, 0, 0.5)'
  ctx.shadowBlur = 6
  ctx.shadowOffsetY = 2
  hexPath(0)
  ctx.fillStyle = '#1E2A3A' // dark infrastructure fill
  ctx.fill()
  ctx.restore()

  // Border ring (status color)
  hexPath(0)
  ctx.strokeStyle = STATUS_RGB[status]
  ctx.lineWidth = size * 0.04
  ctx.lineJoin = 'round'
  ctx.stroke()

  // WiFi icon — three concentric arcs + dot at the base
  const iconScale = size * 0.32
  const dotY = cy + iconScale * 0.35
  const arcX = cx
  const arcY = dotY

  ctx.strokeStyle = '#E6E6E6'
  ctx.lineWidth = size * 0.035
  ctx.lineCap = 'round'

  // Outer arc
  ctx.beginPath()
  ctx.arc(arcX, arcY, iconScale * 0.85, Math.PI * 1.15, Math.PI * 1.85)
  ctx.stroke()
  // Middle arc
  ctx.beginPath()
  ctx.arc(arcX, arcY, iconScale * 0.55, Math.PI * 1.15, Math.PI * 1.85)
  ctx.stroke()
  // Inner arc
  ctx.beginPath()
  ctx.arc(arcX, arcY, iconScale * 0.25, Math.PI * 1.15, Math.PI * 1.85)
  ctx.stroke()
  // Base dot
  ctx.fillStyle = '#E6E6E6'
  ctx.beginPath()
  ctx.arc(arcX, arcY, size * 0.03, 0, Math.PI * 2)
  ctx.fill()

  // Status corner dot (bottom-right of hex) — redundant signal for colorblind
  const cornerX = cx + r * 0.55
  const cornerY = cy + r * 0.55
  ctx.fillStyle = STATUS_RGB[status]
  ctx.beginPath()
  ctx.arc(cornerX, cornerY, size * 0.06, 0, Math.PI * 2)
  ctx.fill()

  return canvas
}

// Texture cache keyed by status — three statuses × one texture each.
const apTextureCache = new Map<AccessPointStatus, THREE.Texture>()

function getCachedAccessPointTexture(status: AccessPointStatus): THREE.Texture {
  const cached = apTextureCache.get(status)
  if (cached) return cached
  const canvas = drawAccessPointTexture(status)
  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.minFilter = THREE.LinearFilter
  tex.magFilter = THREE.LinearFilter
  tex.generateMipmaps = false
  tex.needsUpdate = true
  apTextureCache.set(status, tex)
  return tex
}

function clearAccessPointTextures(): void {
  apTextureCache.forEach(t => t.dispose())
  apTextureCache.clear()
}

// Compact label pill matching the LocationSprite style but at a smaller scale.
function buildLabelTexture(name: string): { texture: THREE.Texture; width: number } {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')!
  const s = 4
  const fontSize = 20 * s
  const paddingH = 16 * s
  const paddingV = 10 * s

  ctx.font = `600 ${fontSize}px Inter, -apple-system, BlinkMacSystemFont, sans-serif`
  const nameW = ctx.measureText(name).width
  const totalW = paddingH + nameW + paddingH
  const totalH = fontSize + paddingV * 2
  const borderRadius = totalH / 2

  canvas.width = totalW
  canvas.height = totalH

  ctx.save()
  ctx.shadowColor = 'rgba(0, 0, 0, 0.5)'
  ctx.shadowBlur = 8 * s
  ctx.shadowOffsetY = 2 * s
  ctx.beginPath()
  ctx.roundRect(0, 0, totalW, totalH, borderRadius)
  ctx.fillStyle = 'rgba(30, 36, 51, 0.85)'
  ctx.fill()
  ctx.restore()

  ctx.beginPath()
  ctx.roundRect(0, 0, totalW, totalH, borderRadius)
  ctx.fillStyle = 'rgba(30, 36, 51, 0.85)'
  ctx.fill()

  ctx.beginPath()
  ctx.roundRect(0, 0, totalW, totalH, borderRadius)
  ctx.strokeStyle = 'rgba(75, 85, 99, 0.5)'
  ctx.lineWidth = 1.5 * s
  ctx.stroke()

  ctx.fillStyle = '#FFFFFF'
  ctx.font = `600 ${fontSize}px Inter, -apple-system, BlinkMacSystemFont, sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(name, totalW / 2, totalH / 2)

  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.minFilter = THREE.LinearFilter
  tex.magFilter = THREE.LinearFilter
  tex.generateMipmaps = false
  tex.needsUpdate = true
  return { texture: tex, width: LABEL_HEIGHT * (totalW / totalH) }
}

const AccessPointSprite: React.FC<{ ap: AccessPoint }> = React.memo(({ ap }) => {
  const { onMarkerDoubleClick } = useMarkerActions()
  const [hovered, setHovered] = useState(false)
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const hexTexture = useMemo(
    () => getCachedAccessPointTexture(ap.status),
    [ap.status],
  )

  const hexMaterial = useMemo(() => {
    const mat = new THREE.SpriteMaterial({
      map: hexTexture,
      transparent: true,
      depthWrite: false,
      sizeAttenuation: true,
    })
    mat.toneMapped = false
    return mat
  }, [hexTexture])

  const { labelMaterial, labelWidth } = useMemo(() => {
    const { texture, width } = buildLabelTexture(ap.name)
    const mat = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthWrite: false,
      sizeAttenuation: true,
    })
    mat.toneMapped = false
    return { labelMaterial: mat, labelWidth: width }
  }, [ap.name])

  const handlePointerEnter = () => {
    document.body.style.cursor = 'pointer'
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current)
    hoverTimerRef.current = setTimeout(() => setHovered(true), HOVER_DELAY_MS)
  }
  const handlePointerLeave = () => {
    document.body.style.cursor = 'default'
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current)
      hoverTimerRef.current = null
    }
    setHovered(false)
  }
  useEffect(() => () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current)
    document.body.style.cursor = 'default'
  }, [])

  const handleDoubleClick = (e: any) => {
    e.stopPropagation()
    onMarkerDoubleClick(ap.position)
  }

  return (
    <group position={[ap.position.x, ap.position.y, ap.position.z]}>
      <sprite
        material={hexMaterial}
        scale={[MARKER_SIZE, MARKER_SIZE, 1]}
        position={[0, 0, MARKER_Z_OFFSET]}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        onDoubleClick={handleDoubleClick}
      />
      <sprite
        material={labelMaterial}
        scale={[labelWidth, LABEL_HEIGHT, 1]}
        position={[0, 0, LABEL_Z_OFFSET]}
      />
      <MarkerHoverLabel
        visible={hovered}
        position={[0, 0, MARKER_Z_OFFSET + MARKER_SIZE]}
        primary={ap.name}
        secondary={`Access point · ${AP_STATUS_LABEL[ap.status]}`}
      />
    </group>
  )
})

AccessPointSprite.displayName = 'AccessPointSprite'

export const AccessPointMarkers: React.FC = () => {
  const accessPointsRecord = useAppSelector(s => s.rtls.accessPoints)
  const filterLevel        = useAppSelector(s => s.rtls.filterLevel)
  const panelFilter        = useAppSelector(s => s.rtls.panelFilter)

  const accessPoints = useMemo(() => {
    const all = Object.values(accessPointsRecord)
    if (filterLevel === 'all') return all
    return all.filter(ap => ap.level === filterLevel)
  }, [accessPointsRecord, filterLevel])

  useEffect(() => {
    return () => {
      clearAccessPointTextures()
    }
  }, [])

  // Visibility check AFTER all hooks — early return between hooks would
  // change the hook count across renders and crash the R3F canvas.
  if (panelFilter !== 'all' && panelFilter !== 'access_point') return null

  return (
    <group>
      {accessPoints.map(ap => (
        <AccessPointSprite key={ap.id} ap={ap} />
      ))}
    </group>
  )
}

AccessPointMarkers.displayName = 'AccessPointMarkers'
